/** @jsxImportSource react */
'use client';

import { useEffect, useRef } from 'react';

// A silent, looping product clip (demo-studio footage). One shared primitive
// so every landing-page clip behaves identically:
//
// - `eager` (heroes, above the fold): autoplays immediately, preloads metadata.
// - default (step cards, below the fold): shows only the poster until the
//   clip scrolls near the viewport, then loads + plays. Keeps a clip-heavy
//   page from downloading megabytes of video nobody scrolled to.
//
// The clip is decorative motion with no audio — `aria-label` describes it for
// screen readers, and it never captures focus or input.
interface Props {
  src: string;
  /** Still frame shown before the clip loads / if autoplay is blocked. */
  poster?: string;
  /** Optional extra source for broader codec support, e.g. a .webm. */
  webmSrc?: string;
  /** Describes the clip for screen readers (the clip has no audio). */
  label: string;
  className?: string;
  /** Load + autoplay immediately (use for above-the-fold heroes only). */
  eager?: boolean;
}

export function DemoClip({
  className,
  eager = false,
  label,
  poster,
  src,
  webmSrc,
}: Props) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = ref.current;

    if (!video || eager) return;
    // Stay observed for the page's lifetime: browsers pause offscreen videos
    // and don't resume them, so a one-shot observer leaves clips frozen after
    // the user scrolls away and back. Play on enter (first play() also starts
    // the preload="none" load), pause on leave.
    const io = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            // Muted inline autoplay is allowed everywhere, but guard anyway —
            // if play is blocked the poster stays.
            video.play().catch(() => undefined);
          } else {
            video.pause();
          }
        }
      },
      { rootMargin: '200px' }
    );

    io.observe(video);

    return () => io.disconnect();
  }, [eager]);

  return (
    <video
      ref={ref}
      loop
      muted
      playsInline
      aria-label={label}
      autoPlay={eager}
      className={className}
      poster={poster}
      preload={eager ? 'metadata' : 'none'}
    >
      {webmSrc && <source src={webmSrc} type="video/webm" />}
      <source src={src} type="video/mp4" />
    </video>
  );
}
